import { PERMISSIONS, updateUserRequestSchema } from "@procuremate/shared";
import { ArrowLeft, Trash2 } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router";
import { useHasPermission } from "@/app/require-permission";
import { FormError, FormNotice } from "@/components/layout/auth-layout";
import { Button } from "@/components/ui/button";
import { SelectField } from "@/components/ui/select-field";
import { TextField } from "@/components/ui/text-field";
import { useCurrentUser } from "@/features/auth/api";
import { adminErrorMessage } from "./admin-error";
import { Card } from "./admin-layout";
import {
  type UserAction,
  useGrantRole,
  useOrg,
  useRevokeRole,
  useRoles,
  useUpdateUser,
  useUser,
  useUserAction,
} from "./api";
import type { InviteResultState } from "./invite-user-page";
import { EMPTY_GRANT, RoleGrantFields, toRoleGrant } from "./role-grant-fields";
import { StatusBadge } from "./status-badge";

const ACTION_CONFLICT: Record<UserAction, string> = {
  invite: "admin.errors.notInvited",
  deactivate: "admin.errors.lastAdminOrSelf",
  reactivate: "admin.errors.conflict",
  "sessions/revoke": "admin.errors.conflict",
};

export function UserDetailPage() {
  const { t, i18n } = useTranslation();
  const { id = "" } = useParams();
  const location = useLocation();
  const invited = (location.state as InviteResultState | null)?.invited;
  const canManage = useHasPermission(PERMISSIONS.USERS_MANAGE);
  const me = useCurrentUser();
  const user = useUser(id);
  const heading = useRef<HTMLHeadingElement>(null);

  // Move focus to the page heading once the user loads (screen readers announce the new page).
  const loadedId = user.data?.id;
  useEffect(() => {
    if (loadedId) heading.current?.focus();
  }, [loadedId]);

  if (user.isPending) return <p role="status">{t("app.loading")}</p>;
  if (user.isError || !user.data) {
    return <FormError message={adminErrorMessage(user.error, t) ?? t("admin.errors.notFound")} />;
  }
  const data = user.data;
  const dateTime = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" });

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/admin/users" className="inline-flex items-center gap-1 text-sm text-link hover:underline">
          <ArrowLeft aria-hidden="true" className="h-4 w-4" />
          {t("admin.users.backToList")}
        </Link>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <h1 ref={heading} tabIndex={-1} className="text-2xl font-bold text-primary outline-none">
            {data.displayName}
          </h1>
          <StatusBadge status={data.status} />
        </div>
        <p className="mt-1 break-all text-fg-muted">{data.email}</p>
        <p className="mt-1 text-sm text-fg-muted">
          {t("admin.users.signInMethod")}: {t(`admin.users.signInMethods.${data.provider}`)}
        </p>
        <p className="mt-1 text-sm text-fg-muted">
          {t("admin.users.lastLogin")}:{" "}
          {data.lastLoginAt ? dateTime.format(new Date(data.lastLoginAt)) : t("admin.users.never")}
        </p>
      </div>

      {invited ? (
        data.provider === "ENTRA" ? (
          <FormNotice message={t("admin.invite.microsoftCreated", { email: data.email })} />
        ) : invited.inviteSent ? (
          <FormNotice message={t("admin.invite.sent", { email: data.email })} />
        ) : (
          <FormError message={t("admin.invite.notSent")} />
        )
      ) : null}

      {canManage ? <ProfileCard key={data.id} user={data} /> : null}
      {canManage ? (
        <AccessCard userId={data.id} status={data.status} provider={data.provider} isSelf={me.data?.id === data.id} />
      ) : null}
      <RolesCard userId={data.id} roles={data.roles} canManage={canManage} />
    </div>
  );
}

function ProfileCard({ user }: { user: { id: string; displayName: string; locale: "it" | "en" } }) {
  const { t } = useTranslation();
  const update = useUpdateUser(user.id);
  const [displayName, setDisplayName] = useState(user.displayName);
  const [locale, setLocale] = useState(user.locale);
  const [nameError, setNameError] = useState<string>();

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const parsed = updateUserRequestSchema.safeParse({ displayName, locale });
    if (!parsed.success) {
      setNameError(t("admin.users.nameRequired"));
      return;
    }
    setNameError(undefined);
    update.mutate(parsed.data);
  }

  return (
    <Card title={t("admin.users.profile")}>
      <form onSubmit={onSubmit} noValidate className="grid gap-4 sm:grid-cols-[1fr_14rem_auto] sm:items-end">
        <TextField
          id="profile-name"
          label={t("admin.users.name")}
          value={displayName}
          error={nameError}
          onChange={(event) => {
            setDisplayName(event.target.value);
            setNameError(undefined);
          }}
        />
        <SelectField
          id="profile-locale"
          label={t("admin.users.language")}
          value={locale}
          options={[
            { value: "it", label: t("admin.languages.it") },
            { value: "en", label: t("admin.languages.en") },
          ]}
          onChange={(event) => setLocale(event.target.value as "it" | "en")}
        />
        <Button type="submit" variant="secondary" disabled={update.isPending}>
          {update.isPending ? t("auth.saving") : t("admin.save")}
        </Button>
      </form>
      <div aria-live="polite">
        {update.isSuccess ? <p className="mt-3 text-sm text-success">{t("admin.saved")}</p> : null}
      </div>
      <FormError message={adminErrorMessage(update.error, t)} />
    </Card>
  );
}

function AccessCard({
  userId,
  status,
  provider,
  isSelf,
}: {
  userId: string;
  status: "INVITED" | "ACTIVE" | "DISABLED";
  provider: "LOCAL" | "ENTRA";
  isSelf: boolean;
}) {
  const { t } = useTranslation();
  const action = useUserAction(userId);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState<string>();
  const confirmText = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (confirming) confirmText.current?.focus();
  }, [confirming]);

  function run(kind: UserAction, doneKey: string) {
    setDone(undefined);
    action.mutate(kind, {
      onSuccess: () => {
        setDone(t(doneKey));
        setConfirming(false);
      },
    });
  }

  return (
    <Card title={t("admin.access.title")}>
      <div className="flex flex-wrap gap-3">
        {status === "INVITED" && provider === "LOCAL" ? (
          <Button
            variant="secondary"
            disabled={action.isPending}
            onClick={() => run("invite", "admin.access.inviteResent")}
          >
            {t("admin.access.resendInvite")}
          </Button>
        ) : null}
        {status === "ACTIVE" ? (
          <Button
            variant="secondary"
            disabled={action.isPending}
            onClick={() => run("sessions/revoke", "admin.access.sessionsRevoked")}
          >
            {t("admin.access.signOutEverywhere")}
          </Button>
        ) : null}
        {status === "DISABLED" ? (
          <Button disabled={action.isPending} onClick={() => run("reactivate", "admin.access.reactivated")}>
            {t("admin.access.reactivate")}
          </Button>
        ) : null}
        {status !== "DISABLED" && !isSelf && !confirming ? (
          <Button variant="danger-secondary" onClick={() => setConfirming(true)}>
            {t("admin.access.deactivate")}
          </Button>
        ) : null}
      </div>

      {confirming ? (
        <div className="mt-4 rounded-md bg-danger-bg p-4">
          {/* Focused on open: the question is read out, and Tab reaches the two choices next. */}
          <p ref={confirmText} tabIndex={-1} className="text-sm text-danger outline-none">
            {t("admin.access.deactivateConfirm")}
          </p>
          <div className="mt-3 flex flex-wrap gap-3">
            <Button
              variant="danger"
              disabled={action.isPending}
              onClick={() => run("deactivate", "admin.access.deactivated")}
            >
              {t("admin.access.deactivateYes")}
            </Button>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              {t("admin.cancel")}
            </Button>
          </div>
        </div>
      ) : null}

      {isSelf ? <p className="mt-3 text-sm text-fg-muted">{t("admin.access.selfNote")}</p> : null}
      <div aria-live="polite">{done ? <p className="mt-3 text-sm text-success">{done}</p> : null}</div>
      <FormError message={adminErrorMessage(action.error, t, action.variables && ACTION_CONFLICT[action.variables])} />
    </Card>
  );
}

function RolesCard({
  userId,
  roles,
  canManage,
}: {
  userId: string;
  roles: { id: string; roleKey: string; roleName: string; scopeType: string; scopeLabel: string | null }[];
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const roleList = useRoles();
  const org = useOrg();
  const grant = useGrantRole(userId);
  const revoke = useRevokeRole(userId);
  const [draft, setDraft] = useState(EMPTY_GRANT);
  const [draftError, setDraftError] = useState<string>();

  function onAdd(event: FormEvent) {
    event.preventDefault();
    const roleGrant = toRoleGrant(draft, roleList.data ?? []);
    if (!roleGrant) {
      setDraftError(t("admin.roles.completeRole"));
      return;
    }
    setDraftError(undefined);
    grant.mutate(roleGrant, { onSuccess: () => setDraft(EMPTY_GRANT) });
  }

  return (
    <Card title={t("admin.roles.title")}>
      {roles.length === 0 ? <p className="text-fg-muted">{t("admin.roles.none")}</p> : null}
      <ul className="divide-y divide-border">
        {roles.map((role) => {
          const name = t(`admin.roleNames.${role.roleKey}`, { defaultValue: role.roleName });
          const scope = role.scopeType === "GLOBAL" ? t("admin.roles.orgWideShort") : (role.scopeLabel ?? "—");
          return (
            <li key={role.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div>
                <p className="font-semibold">{name}</p>
                <p className="text-sm text-fg-muted">{scope}</p>
              </div>
              {canManage ? (
                <Button
                  variant="ghost"
                  disabled={revoke.isPending}
                  aria-label={t("admin.roles.removeLabel", { role: name, scope })}
                  onClick={() => revoke.mutate(role.id)}
                >
                  <Trash2 aria-hidden="true" className="h-4 w-4" />
                  {t("admin.roles.remove")}
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
      <FormError message={adminErrorMessage(revoke.error, t, "admin.errors.lastAdmin")} />

      {canManage ? (
        <form onSubmit={onAdd} noValidate className="mt-6 flex flex-col gap-4 border-t border-border pt-6">
          <h3 className="text-base font-semibold text-fg">{t("admin.roles.add")}</h3>
          <RoleGrantFields
            idPrefix="grant"
            value={draft}
            onChange={(next) => {
              setDraft(next);
              setDraftError(undefined);
            }}
            roles={roleList.data ?? []}
            org={org.data}
            error={draftError}
          />
          <div>
            <Button type="submit" variant="secondary" disabled={grant.isPending}>
              {t("admin.roles.addSubmit")}
            </Button>
          </div>
          <FormError message={adminErrorMessage(grant.error, t, "admin.errors.roleExists")} />
        </form>
      ) : null}
    </Card>
  );
}
